// Slice-internal Konva renderer for pasted images (PRD-F4 §TR1.4, §T3.2).
//
// Replaces the legacy `createImageNode` / `updateImageNode` branch that used
// to live in `objects/board-object-renderer.ts` (Z. 580–632). The slice
// version adds the Variante-C dimension correction: when a Fallback-Bbox
// object is rendered (placed before the natural image dimensions are known),
// the renderer asks the host to update the object's width/height to the
// correct aspect ratio once `<img>.onload` fires.
//
// Variante-C correction is gated by a strict heuristic: the object must
// match the Fallback-Bbox shape (`width === height === MAX/scale`). A
// user-resized image will not match and is left untouched (FC19). The
// renderer also guards against undo/delete races (FC20) by asking the
// registry whether the object still exists before mutating it.
//
// Rendering layout — `Konva.Group` containing:
//   index 0: transparent `Konva.Rect` hit-area (so the group catches events;
//            the `Konva.Image` has `listening:false`),
//   index 1: `Konva.Image` whose source is an `HTMLImageElement` decoded
//            from the inline `dataUrl`.

import Konva from 'konva';
import type { SliceRenderContext } from '../../core/object-registry.js';
import type { BoardObject } from '../../core/interfaces/board-object.interface.js';
import type { ImageObject } from './image.interface.js';
import { MAX_IMAGE_DIMENSION_PX } from './image.types.js';

export const IMAGE_HIT_NAME = 'image-hit';
export const IMAGE_IMG_NAME = 'image-img';

const FALLBACK_BBOX_EPSILON = 0.5;

interface RenderContextWithRegistry {
  readonly viewport: SliceRenderContext['viewport'];
  readonly getObject?: (id: string) => BoardObject | undefined;
  readonly updateObject?: (id: string, partial: Partial<BoardObject>) => void;
}

/** Strict Fallback-Bbox heuristic: object is square AND matches MAX/scale. */
function isFallbackBbox(obj: ImageObject, scale: number): boolean {
  const expected = MAX_IMAGE_DIMENSION_PX / scale;
  return (
    Math.abs(obj.width - obj.height) < FALLBACK_BBOX_EPSILON &&
    Math.abs(obj.width - expected) < FALLBACK_BBOX_EPSILON
  );
}

/** Compute aspect-correct dimensions capped to MAX_IMAGE_DIMENSION_PX. */
function correctDimensions(naturalW: number, naturalH: number): { width: number; height: number } {
  const scale = Math.min(1, MAX_IMAGE_DIMENSION_PX / naturalW, MAX_IMAGE_DIMENSION_PX / naturalH);
  return {
    width: Math.round(naturalW * scale),
    height: Math.round(naturalH * scale),
  };
}

/**
 * Attach an HTMLImageElement to a Konva.Image and wire load/error/correction.
 *
 * The source is resolved once as `obj.src ?? obj.dataUrl` (asset reference wins
 * over the inline base64 blob) and stored on the Konva.Image as the
 * `o2dSourceRef` attribute. `updateNode` compares against THAT attribute rather
 * than the `<img>.src` property, because the browser resolves a relative asset
 * URL to an absolute one — a property compare would never match and would
 * rebuild the `<img>` on every update (O2D.W-PRD-042 §TR6, G4).
 *
 * Returns `undefined` when the object carries neither source; no `<img>` is
 * constructed in that case.
 */
function loadImageInto(
  group: Konva.Group,
  kImage: Konva.Image,
  obj: ImageObject,
  ctx: RenderContextWithRegistry,
): HTMLImageElement | undefined {
  const source = obj.src ?? obj.dataUrl;
  if (!source) {
    console.error(`[image.renderer] object ${obj.id} carries neither src nor dataUrl`);
    return undefined;
  }

  const imgEl = new window.Image();
  imgEl.onload = () => {
    // Re-draw once the bitmap is decoded.
    group.getLayer()?.batchDraw();
    // Variante-C: correct Fallback-Bbox dimensions to natural aspect ratio.
    if (!isFallbackBbox(obj, ctx.viewport.scale)) return;
    const live = ctx.getObject?.(obj.id);
    if (!live) return; // object was deleted / undone before load completed
    const corrected = correctDimensions(imgEl.naturalWidth, imgEl.naturalHeight);
    ctx.updateObject?.(obj.id, corrected);
  };
  imgEl.onerror = () => {
    console.error(`[image.renderer] failed to load source for object ${obj.id}`);
  };
  imgEl.src = source;
  kImage.setAttr('o2dSourceRef', source);
  kImage.image(imgEl);
  return imgEl;
}

/** Build a fresh Konva.Group rendering the image. */
export function createNode(obj: ImageObject, ctx: SliceRenderContext): Konva.Group {
  const group = new Konva.Group({
    id: obj.id,
    x: obj.x,
    y: obj.y,
    width: obj.width,
    height: obj.height,
    rotation: obj.rotation,
    name: 'image-obj',
  });

  const hit = new Konva.Rect({
    name: IMAGE_HIT_NAME,
    x: 0,
    y: 0,
    width: obj.width,
    height: obj.height,
    fill: 'rgba(0,0,0,0)',
  });

  const kImage = new Konva.Image({
    name: IMAGE_IMG_NAME,
    x: 0,
    y: 0,
    width: obj.width,
    height: obj.height,
    image: undefined,
    listening: false,
  });

  group.add(hit);
  group.add(kImage);

  loadImageInto(group, kImage, obj, ctx as RenderContextWithRegistry);
  return group;
}

/** Apply changed properties from an updated ImageObject onto its node. */
export function updateNode(node: Konva.Node, obj: ImageObject, ctx: SliceRenderContext): void {
  if (!(node instanceof Konva.Group)) return;

  const hit = node.findOne<Konva.Rect>(`.${IMAGE_HIT_NAME}`);
  if (hit) hit.setAttrs({ width: obj.width, height: obj.height });

  const kImage = node.findOne<Konva.Image>(`.${IMAGE_IMG_NAME}`);
  if (!kImage) return;
  kImage.setAttrs({ width: obj.width, height: obj.height });

  // Compare against the source the node was BUILT from, not the `<img>.src`
  // property (which the browser may have resolved to an absolute URL).
  if (kImage.getAttr('o2dSourceRef') !== (obj.src ?? obj.dataUrl)) {
    loadImageInto(node, kImage, obj, ctx as RenderContextWithRegistry);
  }
}
